/**
 * Lightweight markdown renderer used by the About page and the Projects
 * blog. Deliberately minimal — the admin is a single technical user, so we
 * skip full CommonMark and only support what actually gets typed:
 *
 *   ## Section title          → titled section (Projects only uses this for h2)
 *   blank line                → paragraph break
 *   - bullet                  → unordered list
 *   **bold**  _italic_  `code`  [label](https://url)
 *
 * Anything else is passed through as plain text (React auto-escapes it, so
 * no HTML injection is possible even if the admin pastes something hostile).
 */

/** Split body into <p>s on blank lines. Detects `- ` bullet groups and
 *  renders them as <ul>. Everything else is a paragraph.
 */
export function renderParagraphs(text, keyPrefix = "p") {
  const blocks = (text || "").replace(/\r\n?/g, "\n").split(/\n{2,}/).filter((b) => b.trim() !== "");
  return blocks.map((b, i) => {
    const lines = b.split("\n");
    // If every non-blank line starts with `- `, render as an unordered list.
    const isList = lines.every((l) => l.trim() === "" || l.trim().startsWith("- "));
    if (isList) {
      return (
        <ul key={`${keyPrefix}-${i}`} className="cd-md-list" style={{ margin: "10px 0 0 22px", padding: 0, lineHeight: 1.7 }}>
          {lines.filter((l) => l.trim().startsWith("- ")).map((l, j) => (
            <li key={j} style={{ margin: "4px 0" }}>
              {renderInline(l.replace(/^\s*-\s+/, ""))}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={`${keyPrefix}-${i}`} style={{ margin: i > 0 ? "16px 0 0" : "0" }}>
        {renderInline(b)}
      </p>
    );
  });
}

/** Inline markdown → React nodes. Order matters: links first, then bold,
 *  then italic, then code. Runs sequentially over an array of (string |
 *  node) segments, only splitting the string entries.
 */
export function renderInline(text) {
  let segs = [text];
  const passes = [
    {
      // [label](https://url | mailto:x)
      re: /\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/,
      node: (m, key) => (
        <a
          key={key}
          href={m[2]}
          target={m[2].startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
          style={{ color: "var(--cd-blue-500)", fontWeight: 600 }}
        >
          {m[1]}
        </a>
      ),
    },
    {
      re: /\*\*([^*]+)\*\*/,
      node: (m, key) => <strong key={key} style={{ fontWeight: 700 }}>{m[1]}</strong>,
    },
    {
      re: /_([^_]+)_/,
      node: (m, key) => <em key={key}>{m[1]}</em>,
    },
    {
      re: /`([^`]+)`/,
      node: (m, key) => (
        <code
          key={key}
          style={{
            padding: "2px 6px", borderRadius: 5,
            background: "var(--cd-gray-100, #f4f4f5)",
            color: "var(--cd-navy-950, #03142f)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: "0.92em",
          }}
        >
          {m[1]}
        </code>
      ),
    },
  ];

  let k = 0;
  for (const pass of passes) {
    const next = [];
    for (const seg of segs) {
      if (typeof seg !== "string") { next.push(seg); continue; }
      let s = seg;
      // Iteratively pull out matches so multiple markers per string all render.
      while (true) {
        const m = s.match(pass.re);
        if (!m) { if (s) next.push(s); break; }
        const before = s.slice(0, m.index);
        if (before) next.push(before);
        next.push(pass.node(m, `md-${k++}`));
        s = s.slice(m.index + m[0].length);
      }
    }
    segs = next;
  }
  return segs;
}
